import { useMemo, type ReactNode } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTheme } from "../../theme/ThemeProvider"
import { usePublicInvoice } from "../../hooks/useApi"

export default function PublicInvoiceView() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isFairy = theme === "fairy"

  const { data, isLoading, error } = usePublicInvoice(token)

  const panel = useMemo(() => buildPanel(isFairy), [isFairy])

  if (!token) {
    return <CenteredPanel panel={panel}>Missing invoice reference.</CenteredPanel>
  }

  if (isLoading) {
    return <CenteredPanel panel={panel}>Summoning invoice…</CenteredPanel>
  }

  if (error || !data) {
    return <CenteredPanel panel={panel}>Invoice not found. Contact the merchant for a fresh link.</CenteredPanel>
  }

  const createdDate = data.created_at ? new Date(data.created_at).toLocaleString() : "—"
  const dueDate = data.due_date ? new Date(data.due_date).toLocaleDateString() : "—"

  const handlePayment = () => {
    if (data.hosted_invoice_url) {
      window.location.assign(data.hosted_invoice_url)
    }
  }

  return (
    <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: "clamp(16px, 6vw, 32px)" }}>
      <section style={panel.container}>
        <header className="mono" style={panel.header}>Invoice {data.stripe_invoice_id ?? ""}</header>
        <dl style={{ display: "grid", gap: "12px", margin: 0 }}>
          <DetailRow label="Status" value={data.status.toUpperCase()} isFairy={isFairy} />
          <DetailRow label="Customer" value={data.customer?.name ?? "Customer"} isFairy={isFairy} />
          <DetailRow label="Email" value={data.customer?.email ?? "—"} isFairy={isFairy} />
          <DetailRow label="Created" value={createdDate} isFairy={isFairy} />
          <DetailRow label="Due" value={dueDate} isFairy={isFairy} />
          <DetailRow label="Description" value={data.description ?? "—"} isFairy={isFairy} />
        </dl>
        <div style={{ fontSize: "clamp(22px, 6vw, 28px)", fontWeight: 600 }}>
          {formatPrice(data.amount_cents, data.currency)}
        </div>
        <button
          type="button"
          onClick={handlePayment}
          disabled={!data.hosted_invoice_url}
          className="mono"
          style={primaryButton(isFairy, !data.hosted_invoice_url)}
        >
          Pay now
        </button>
        <button
          type="button"
          onClick={() => navigate("/public/success", { replace: false })}
          className="small"
          style={{
            border: "none",
            background: "transparent",
            color: panel.linkColor,
            cursor: "pointer",
            textTransform: "none" as const,
            minHeight: "44px",
          }}
        >
          Already paid? Continue
        </button>
      </section>
    </main>
  )
}

function CenteredPanel({ panel, children }: { panel: ReturnType<typeof buildPanel>; children: ReactNode }) {
  return (
    <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: "clamp(16px, 6vw, 32px)" }}>
      <section style={panel.container}>
        <div className="small" style={{ textTransform: "none" as const, color: panel.muted.color }}>{children}</div>
      </section>
    </main>
  )
}

function buildPanel(isFairy: boolean) {
  if (isFairy) {
    return {
      container: {
        maxWidth: 420,
        width: "100%",
        background: "rgba(255,255,255,0.95)",
        border: "1px solid rgba(46,214,181,0.35)",
        boxShadow: "0 18px 34px rgba(46,214,181,0.12)",
        padding: "clamp(20px, 5vw, 32px)",
        display: "grid",
        gap: "clamp(12px, 3vw, 18px)",
      },
      header: {
        letterSpacing: "0.18em",
        textTransform: "uppercase" as const,
        color: "#1f2d42",
      },
      muted: {
        color: "#4b5a6d",
      },
      linkColor: "#1f2d42",
    }
  }
  return {
    container: {
      maxWidth: 420,
      width: "100%",
      background: "rgba(18,18,20,0.85)",
      border: "1px solid rgba(255,43,79,0.45)",
      boxShadow: "0 18px 34px rgba(255,43,79,0.18)",
      padding: "clamp(20px, 5vw, 32px)",
      display: "grid",
      gap: "clamp(12px, 3vw, 18px)",
    },
    header: {
      letterSpacing: "0.18em",
      textTransform: "uppercase" as const,
      color: "var(--text)",
    },
    muted: {
      color: "var(--muted)",
    },
    linkColor: "var(--accent)",
  }
}

function DetailRow({ label, value, isFairy }: { label: string; value: string; isFairy: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
      <span className="small" style={{ color: isFairy ? "#1f2d42" : "var(--muted)", textTransform: "uppercase" as const }}>{label}</span>
      <span className="mono" style={{ textAlign: "right", flex: "1 1 auto" }}>{value || "—"}</span>
    </div>
  )
}

function primaryButton(isFairy: boolean, disabled: boolean) {
  return {
    padding: "12px 18px",
    border: "2px solid var(--rail)",
    background: isFairy ? "rgba(244,255,251,0.95)" : "var(--rail)",
    color: isFairy ? "#1f2d42" : "#0a0a0b",
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    minHeight: "44px",
  }
}

function formatPrice(amountCents: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amountCents / 100)
  } catch {
    return `${amountCents / 100} ${currency}`
  }
}
